import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {HeaderItem, helpers} from '@shared/helper/helpers';
import {format} from 'date-fns';
import {SlotModel} from '@app/@core/domain/slot.model';
import {groupBy, mapValues, orderBy} from 'lodash-es';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {SlotManagerStoreService} from '@app/modules/slot/slot-manager/slot-manager-store.service';
import {combineLatest} from 'rxjs';
import {CourseCalendar, Holiday} from '@app/models/course-calendar';
import Slot = SlotModel.Slot;

function groupByLodash(results: any[], firstKey: string, secondKey: string) {
    return mapValues(
        groupBy(results, firstKey),
        (result: any) => groupBy(result, secondKey),
    );
}

interface SlotWrapper {
    date: string;
    hourOfDay: number;
    slot: Slot;
}

interface CourseEvent {
    date: string;
    severity: string;
    name: string;
}

interface DateHeader extends Partial<HeaderItem> {
    key: string;
    title: string;
    events?: CourseEvent[];
}

interface HourlyRow {
    hourOfDay: string;

    [propName: string]: SlotWrapper[] | string;
}

@UntilDestroy()
@Component({
    selector: 'app-slot-grid',
    template: `
        <div id="table-scroll"
             class="table-scroll">
            <div class="table-wrap">
                <table class="main-table">
                    <thead>
                    <tr>
                        <th scope="col"
                            style="width: 50px"></th>

                        <ng-container *ngFor="let column of columns">
                            <th *ngIf="column.key !== 'hourOfDay'"
                                class="table-hea-width">
                                <div class="flex flex-column justify-content-center">
                                    <div class="font-bold"
                                         [class.text-red-900]="column.isWeekEnd">{{ column.title }}
                                    </div>
                                    <div *ngIf="multiple"
                                         class="text-center">
                                        <p-checkbox name="selectDates"
                                                    [value]="column.key"
                                                    [(ngModel)]="selectedDates"
                                                    (ngModelChange)="selectDateChanged()"
                                        >
                                        </p-checkbox>
                                    </div>
                                </div>
                                <div *ngFor="let event of column.events">
                                    <p-tag [severity]="event.severity"
                                           styleClass="mt-1"
                                           [value]="event.name"
                                           [rounded]="false"></p-tag>
                                </div>
                            </th>
                        </ng-container>
                    </tr>
                    </thead>
                    <tbody>

                    <tr *ngFor="let rowData of hourlyRows">
                        <th>
                            {{ rowData['hourOfDay'] }}
                        </th>
                        <td *ngFor="let key of columns| pluck: 'key' | without: ['hourOfDay']">
                            <div *ngIf="rowData[key] as slotWrappers"
                                 class=" my-2 slot-td">
                                <div *ngFor="let sw of slotWrappers"
                                     class="text-center shadow-4 border-round mt-1 p-1 surface-200">
                                    <app-slot-cell
                                        (dblclick)="dblclick(sw)"
                                        [slot]="sw.slot">
                                    </app-slot-cell>
                                    <p-checkbox name="selectSlots"
                                                [value]="sw.slot.id"
                                                [(ngModel)]="selectedIds"
                                                (ngModelChange)="selectSlotsChanged($event)">
                                    </p-checkbox>
                                </div>
                            </div>
                        </td>
                    </tr>

                </table>
            </div>
        </div>
    `,
    styleUrls: ['../../modules/slot/slot-manager/slot-manager.component.scss'],
    styles: []
})
export class SlotGridComponent implements OnChanges {
    @Input() from: Date;
    @Input() to: Date;
    @Input() slots: Slot[] = [];
    @Input() multiple = true;
    @Input() holidays: Holiday[];
    @Input() calendars: CourseCalendar[];

    @Output() selectChanged = new EventEmitter<Slot[]>();
    @Output() dbClick = new EventEmitter<Slot>();

    selectedDates: string[];
    selectedIds: string[] = [];
    columns: DateHeader[];
    hourlyRows: HourlyRow[];

    private slotWrappers: SlotWrapper[];
    private events: { [key: string]: CourseEvent[] };

    constructor() {
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.holidays || changes.calendars) {
            this.events = this.buildEventList(this.holidays, this.calendars);
        }

        if (changes.slots) {
            this.renderSlots(this.slots);
        }
    }

    private renderSlots(slots: SlotModel.Slot[]) {
        this.updateSelectedValue([]);

        slots.forEach(s => s.startDate = new Date(s.startDate));
        slots = orderBy(slots, 'startDate');

        this.slotWrappers = slots.map(slot => {
            return {
                slot,
                date: format(slot.startDate, 'yyyy-MM-dd'),
                hourOfDay: slot.startDate.getHours()
            } as SlotWrapper;
        });

        const hourlyObj = groupByLodash(this.slotWrappers, 'hourOfDay', 'date');
        this.hourlyRows = Object.entries(hourlyObj).map(([hourOfDay, dates]) => {
            // @ts-ignore
            return {...dates, hourOfDay};
        });

        const dateColumns: DateHeader[] = helpers.dateList(this.from, this.to)
            .map((header, index) => {
                const dateFmt = header.date.getDate() === 1 || index === 0 ? 'd MMM' : 'd';
                return {
                    key: header.label,
                    title: format(header.date, dateFmt),
                    events: this.events ? this.events[header.label] : undefined,
                    ...header
                };
            });

        this.columns = [{key: 'hourOfDay', title: 'H'}, ...dateColumns];
    }

    private buildEventList(holidays: Holiday[], calendars: CourseCalendar[]) {
        const severityMap = {
            HOLIDAY: 'warning',
            NOTHOLIDAY: 'success',
            NOTSALE: 'primary'
        };

        const holidayList: CourseEvent[] = holidays?.map(h => {
            return {
                date: format(h.day, 'yyyy-MM-dd'),
                name: h.name,
                severity: 'danger'
            };
        });

        const calendarList: CourseEvent[] = calendars?.map(c => {
            return {
                date: format(c.day, 'yyyy-MM-dd'),
                name: c.name,
                severity: severityMap[c.type]
            };
        });

        return groupBy([...(holidayList ?? []), ...(calendarList ?? [])], 'date');
    }

    selectDateChanged() {
        const slotIds = this.slotWrappers
            .filter(sw => this.selectedDates.includes(sw.date))
            .map(sw => sw.slot.id);

        this.updateSelectedValue(slotIds);
    }

    selectSlotsChanged($event: any) {
        if (this.selectedIds.length) {
            const slotIds = this.multiple ? this.selectedIds : this.selectedIds.slice(-1);
            this.updateSelectedValue(slotIds);
        } else {
            this.updateSelectedValue([]);
        }
    }

    private findSlot(slotId: string) {
        return this.slotWrappers.find(sw => sw.slot.id === slotId);
    }

    checkAll(check: boolean) {
        if (check && this.slots.length === this.selectedIds.length) {
            check = false;
        }

        this.selectedDates = check ? this.columns.filter(c => c.key !== 'hourOfDay').map(c => c.key) : [];
        this.selectDateChanged();
    }

    updateSelectedValue(slotIds: string[]) {
        this.selectedIds = slotIds;
        const slots = slotIds.map(id => this.findSlot(id).slot);
        this.selectChanged.emit(slots);
    }

    dblclick(sw: SlotWrapper) {
        this.dbClick.emit(sw.slot);
    }
}
